import { createClient } from '@supabase/supabase-js';
import { Tour, Review } from '../types';
import { USE_BACKEND_API } from '../config/apiConfig';

// Supabase configuration - Strict .env enforcement for security
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

// Validate URL
if (!supabaseUrl || !supabaseUrl.startsWith('http')) {
  if (process.env.NODE_ENV !== 'production') {
    console.warn('⚠️ Supabase environment variables missing. Ensure REACT_APP_SUPABASE_URL is set.');
  }
}
export const supabase = supabaseUrl && supabaseKey
  ? createClient(supabaseUrl, supabaseKey, {
      auth: {
        autoRefreshToken: !USE_BACKEND_API,
        persistSession: !USE_BACKEND_API,
        detectSessionInUrl: !USE_BACKEND_API,
        flowType: 'pkce'
      }
    })
  : ({} as any); // Provide empty mock if running natively without Supabase

// Database service interface - this will make migration easier
interface DatabaseService {
  getAllTours(): Promise<Tour[]>;
  getActiveTours(): Promise<Tour[]>;
  getTourById(id: string): Promise<Tour | null>;
  addTour(tour: Omit<Tour, 'id' | 'createdAt' | 'updatedAt'>): Promise<Tour>;
  updateTour(id: string, tour: Omit<Tour, 'id' | 'createdAt' | 'updatedAt'>): Promise<Tour | null>;
  deleteTour(id: string): Promise<boolean>;
  getTourStats(): Promise<{
    totalTours: number;
    activeTours: number;
    totalRevenue: number;
    avgRating: number;
  }>;
  getTourReviews(tourId: string): Promise<Review[]>;
  addReview(tourId: string, reviewData: {
    userName: string;
    rating: number;
    title: string;
    comment: string;
  }): Promise<Review>;
  updateTourRating(tourId: string): Promise<void>;
}

class SupabaseTourService implements DatabaseService {
  // Get all tours
  async getAllTours(): Promise<Tour[]> {
    try {
      const { data, error } = await supabase
        .from('tours')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching tours:', error);
        return [];
      }

      // Convert snake_case to camelCase for frontend
      const tours: Tour[] = (data || []).map((dbTour: any) => ({
        id: dbTour.id,
        title: dbTour.title,
        description: dbTour.description,
        shortDescription: dbTour.short_description,
        duration: dbTour.duration,
        price: dbTour.price,
        currency: dbTour.currency,
        difficulty: dbTour.difficulty,
        category: dbTour.category,
        images: dbTour.images,
        highlights: dbTour.highlights,
        itinerary: dbTour.itinerary,
        includes: dbTour.includes,
        excludes: dbTour.excludes,
        requirements: dbTour.requirements,
        maxParticipants: dbTour.max_participants,
        minParticipants: dbTour.min_participants,
        rating: dbTour.rating,
        reviewCount: dbTour.review_count,
        location: dbTour.location,
        guides: dbTour.guides,
        availability: dbTour.availability,
        active: dbTour.active,
        createdAt: dbTour.created_at,
        updatedAt: dbTour.updated_at
      }));

      return tours;
    } catch (error) {
      console.error('Error in getAllTours:', error);
      return [];
    }
  }

  // Get only active tours
  async getActiveTours(): Promise<Tour[]> {
    try {
      const { data, error } = await supabase
        .from('tours')
        .select('*')
        .eq('active', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching active tours:', error);
        return [];
      }

      // Convert snake_case to camelCase for frontend
      const tours: Tour[] = (data || []).map((dbTour: any) => ({
        id: dbTour.id,
        title: dbTour.title,
        description: dbTour.description,
        shortDescription: dbTour.short_description,
        duration: dbTour.duration,
        price: dbTour.price,
        currency: dbTour.currency,
        difficulty: dbTour.difficulty,
        category: dbTour.category,
        images: dbTour.images,
        highlights: dbTour.highlights,
        itinerary: dbTour.itinerary,
        includes: dbTour.includes,
        excludes: dbTour.excludes,
        requirements: dbTour.requirements,
        maxParticipants: dbTour.max_participants,
        minParticipants: dbTour.min_participants,
        rating: dbTour.rating,
        reviewCount: dbTour.review_count,
        location: dbTour.location,
        guides: dbTour.guides,
        availability: dbTour.availability,
        active: dbTour.active,
        createdAt: dbTour.created_at,
        updatedAt: dbTour.updated_at
      }));

      return tours;
    } catch (error) {
      console.error('Error in getActiveTours:', error);
      return [];
    }
  }

  // Get tour by ID
  async getTourById(id: string): Promise<Tour | null> {
    try {
      const { data, error } = await supabase
        .from('tours')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching tour:', error);
        return null;
      }

      // Convert snake_case to camelCase for frontend
      const tour: Tour = {
        id: data.id,
        title: data.title,
        description: data.description,
        shortDescription: data.short_description,
        duration: data.duration,
        price: data.price,
        currency: data.currency,
        difficulty: data.difficulty,
        category: data.category,
        images: data.images,
        highlights: data.highlights,
        itinerary: data.itinerary,
        includes: data.includes,
        excludes: data.excludes,
        requirements: data.requirements,
        maxParticipants: data.max_participants,
        minParticipants: data.min_participants,
        rating: data.rating,
        reviewCount: data.review_count,
        location: data.location,
        guides: data.guides,
        availability: data.availability,
        active: data.active,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      };

      return tour;
    } catch (error) {
      console.error('Error in getTourById:', error);
      return null;
    }
  }

  // Add new tour
  async addTour(tourData: Omit<Tour, 'id' | 'createdAt' | 'updatedAt'>): Promise<Tour> {
    try {
      const now = new Date().toISOString();

      // Convert camelCase to snake_case for database
      const dbTour = {
        id: crypto.randomUUID(), // Generate UUID
        title: tourData.title,
        description: tourData.description,
        short_description: tourData.shortDescription,
        duration: tourData.duration,
        price: tourData.price,
        currency: tourData.currency,
        difficulty: tourData.difficulty,
        category: tourData.category,
        images: tourData.images,
        highlights: tourData.highlights,
        itinerary: tourData.itinerary,
        includes: tourData.includes,
        excludes: tourData.excludes,
        requirements: tourData.requirements,
        max_participants: tourData.maxParticipants,
        min_participants: tourData.minParticipants,
        rating: tourData.rating,
        review_count: tourData.reviewCount,
        location: tourData.location,
        guides: tourData.guides,
        availability: tourData.availability,
        active: tourData.active,
        created_at: now,
        updated_at: now
      };

      console.log('Inserting tour to database:', dbTour);

      const { data, error } = await supabase
        .from('tours')
        .insert([dbTour])
        .select()
        .single();

      if (error) {
        console.error('Error adding tour:', error);
        console.error('Error details:', error);

        // Handle category constraint error specifically
        if (error.code === '23514' && error.message.includes('tours_category_check')) {
          console.warn('Category constraint violation detected. Attempting fallback with default category...');

          // Try again with a default category
          const fallbackDbTour = {
            ...dbTour,
            category: 'Safari' // Use a default category that should pass the constraint
          };

          const { data: fallbackData, error: fallbackError } = await supabase
            .from('tours')
            .insert([fallbackDbTour])
            .select()
            .single();

          if (fallbackError) {
            console.error('Fallback insert also failed:', fallbackError);
            throw new Error('Failed to add tour - category constraint violation');
          }

          console.log('Successfully added tour with fallback category');
          // Continue with the fallback data
          const frontendTour: Tour = {
            id: fallbackData.id,
            title: fallbackData.title,
            description: fallbackData.description,
            shortDescription: fallbackData.short_description,
            duration: fallbackData.duration,
            price: fallbackData.price,
            currency: fallbackData.currency,
            difficulty: fallbackData.difficulty,
            category: fallbackData.category,
            images: fallbackData.images,
            highlights: fallbackData.highlights,
            itinerary: fallbackData.itinerary,
            includes: fallbackData.includes,
            excludes: fallbackData.excludes,
            requirements: fallbackData.requirements,
            maxParticipants: fallbackData.max_participants,
            minParticipants: fallbackData.min_participants,
            rating: fallbackData.rating,
            reviewCount: fallbackData.review_count,
            location: fallbackData.location,
            guides: fallbackData.guides,
            availability: fallbackData.availability,
            active: fallbackData.active,
            createdAt: fallbackData.created_at,
            updatedAt: fallbackData.updated_at
          };

          return frontendTour;
        }

        throw new Error('Failed to add tour');
      }

      // Convert back to camelCase for frontend
      const frontendTour: Tour = {
        id: data.id,
        title: data.title,
        description: data.description,
        shortDescription: data.short_description,
        duration: data.duration,
        price: data.price,
        currency: data.currency,
        difficulty: data.difficulty,
        category: data.category,
        images: data.images,
        highlights: data.highlights,
        itinerary: data.itinerary,
        includes: data.includes,
        excludes: data.excludes,
        requirements: data.requirements,
        maxParticipants: data.max_participants,
        minParticipants: data.min_participants,
        rating: data.rating,
        reviewCount: data.review_count,
        location: data.location,
        guides: data.guides,
        availability: data.availability,
        active: data.active,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      };

      // Trigger storage event for real-time updates
      window.dispatchEvent(new CustomEvent('tourUpdated'));

      return frontendTour;
    } catch (error) {
      console.error('Error in addTour:', error);
      throw error;
    }
  }

  // Update existing tour
  async updateTour(id: string, tourData: Omit<Tour, 'id' | 'createdAt' | 'updatedAt'>): Promise<Tour | null> {
    try {
      const now = new Date().toISOString();

      // Convert camelCase to snake_case for database
      const dbTour = {
        title: tourData.title,
        description: tourData.description,
        short_description: tourData.shortDescription,
        duration: tourData.duration,
        price: tourData.price,
        currency: tourData.currency,
        difficulty: tourData.difficulty,
        category: tourData.category,
        images: tourData.images,
        highlights: tourData.highlights,
        itinerary: tourData.itinerary,
        includes: tourData.includes,
        excludes: tourData.excludes,
        requirements: tourData.requirements,
        max_participants: tourData.maxParticipants,
        min_participants: tourData.minParticipants,
        rating: tourData.rating,
        review_count: tourData.reviewCount,
        location: tourData.location,
        guides: tourData.guides,
        availability: tourData.availability,
        active: tourData.active,
        updated_at: now
      };

      console.log('Updating tour in database:', { id, dbTour });

      const { data, error } = await supabase
        .from('tours')
        .update(dbTour)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating tour:', error);
        console.error('Error details:', error);
        console.error('Full error object:', error);

        // Handle category constraint error specifically
        if (error.code === '23514' && error.message.includes('tours_category_check')) {
          console.warn('Category constraint violation detected. Attempting fallback with default category...');

          // Try again with a default category
          const fallbackDbTour = {
            ...dbTour,
            category: 'Safari' // Use a default category that should pass the constraint
          };

          const { data: fallbackData, error: fallbackError } = await supabase
            .from('tours')
            .update(fallbackDbTour)
            .eq('id', id)
            .select()
            .single();

          if (fallbackError) {
            console.error('Fallback update also failed:', fallbackError);
            return null;
          }

          console.log('Successfully updated tour with fallback category');
          // Continue with the fallback data
          const frontendTour: Tour = {
            id: fallbackData.id,
            title: fallbackData.title,
            description: fallbackData.description,
            shortDescription: fallbackData.short_description,
            duration: fallbackData.duration,
            price: fallbackData.price,
            currency: fallbackData.currency,
            difficulty: fallbackData.difficulty,
            category: fallbackData.category,
            images: fallbackData.images,
            highlights: fallbackData.highlights,
            itinerary: fallbackData.itinerary,
            includes: fallbackData.includes,
            excludes: fallbackData.excludes,
            requirements: fallbackData.requirements,
            maxParticipants: fallbackData.max_participants,
            minParticipants: fallbackData.min_participants,
            rating: fallbackData.rating,
            reviewCount: fallbackData.review_count,
            location: fallbackData.location,
            guides: fallbackData.guides,
            availability: fallbackData.availability,
            active: fallbackData.active,
            createdAt: fallbackData.created_at,
            updatedAt: fallbackData.updated_at
          };

          return frontendTour;
        }

        return null;
      }

      // Convert back to camelCase for frontend
      const frontendTour: Tour = {
        id: data.id,
        title: data.title,
        description: data.description,
        shortDescription: data.short_description,
        duration: data.duration,
        price: data.price,
        currency: data.currency,
        difficulty: data.difficulty,
        category: data.category,
        images: data.images,
        highlights: data.highlights,
        itinerary: data.itinerary,
        includes: data.includes,
        excludes: data.excludes,
        requirements: data.requirements,
        maxParticipants: data.max_participants,
        minParticipants: data.min_participants,
        rating: data.rating,
        reviewCount: data.review_count,
        location: data.location,
        guides: data.guides,
        availability: data.availability,
        active: data.active,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      };

      // Trigger storage event for real-time updates
      window.dispatchEvent(new CustomEvent('tourUpdated'));

      return frontendTour;
    } catch (error) {
      console.error('Error in updateTour:', error);
      return null;
    }
  }

  // Delete tour
  async deleteTour(id: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('tours')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting tour:', error);
        return false;
      }

      // Trigger storage event for real-time updates
      window.dispatchEvent(new CustomEvent('tourUpdated'));

      return true;
    } catch (error) {
      console.error('Error in deleteTour:', error);
      return false;
    }
  }

  // Get tour statistics
  async getTourStats(): Promise<{
    totalTours: number;
    activeTours: number;
    totalRevenue: number;
    avgRating: number;
  }> {
    try {
      const { data: allTours, error: allError } = await supabase
        .from('tours')
        .select('*');

      if (allError) {
        console.error('Error fetching tour stats:', allError);
        return { totalTours: 0, activeTours: 0, totalRevenue: 0, avgRating: 0 };
      }

      const tours = allTours || [];
      const activeTours = tours.filter((tour: any) => tour.active);

      const totalRevenue = tours.reduce((sum: any, tour: any) => sum + (tour.price || 0), 0);
      const avgRating = tours.length > 0
        ? tours.reduce((sum: any, tour: any) => sum + (tour.rating || 0), 0) / tours.length
        : 0;

      return {
        totalTours: tours.length,
        activeTours: activeTours.length,
        totalRevenue,
        avgRating: Math.round(avgRating * 10) / 10
      };
    } catch (error) {
      console.error('Error in getTourStats:', error);
      return { totalTours: 0, activeTours: 0, totalRevenue: 0, avgRating: 0 };
    }
  }

  // Get reviews for a tour
  async getTourReviews(tourId: string): Promise<Review[]> {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('tour_id', tourId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching reviews:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getTourReviews:', error);
      throw error;
    }
  }

  // Add a review for a tour
  async addReview(tourId: string, reviewData: {
    userName: string;
    rating: number;
    title: string;
    comment: string;
  }): Promise<Review> {
    try {
      const review = {
        tour_id: tourId,
        user_id: 'anonymous',
        user_name: reviewData.userName,
        rating: reviewData.rating,
        title: reviewData.title,
        comment: reviewData.comment,
        helpful: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('reviews')
        .insert([review])
        .select()
        .single();

      if (error) {
        console.error('Error adding review:', error);
        throw error;
      }

      // Convert to Review type
      return {
        id: data.id,
        tourId: data.tour_id,
        userId: data.user_id,
        userName: data.user_name,
        rating: data.rating,
        title: data.title,
        comment: data.comment,
        helpful: data.helpful,
        createdAt: data.created_at
      };
    } catch (error) {
      console.error('Error in addReview:', error);
      throw error;
    }
  }

  // Update tour rating based on reviews
  async updateTourRating(tourId: string): Promise<void> {
    try {
      // Get all reviews for this tour
      const reviews = await this.getTourReviews(tourId);

      if (reviews.length > 0) {
        const avgRating = reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length;

        // Update the tour's rating and review count
        const { error } = await supabase
          .from('tours')
          .update({
            rating: Math.round(avgRating * 10) / 10, // Round to 1 decimal
          })
          .eq('id', tourId);

        if (error) {
          console.error('Error updating tour rating:', error);
          throw error;
        }
      }
    } catch (error) {
      console.error('Error in updateTourRating:', error);
      throw error;
    }
  }
}

// Create service instance
export const supabaseTourService = new SupabaseTourService();

// Create guides table if it doesn't exist
export const createGuidesTable = async () => {
  try {
    console.log('🔧 Creating guides table...');

    // Check if guides table exists by trying to query it
    const { error } = await supabase
      .from('guides')
      .select('id')
      .limit(1);

    if (error && error.code === 'PGRST116') {
      // Table doesn't exist, create it
      console.log('📋 Guides table not found, creating...');

      // Note: In Supabase, you would typically create tables through the SQL editor
      // This is a placeholder - you'll need to run this SQL in your Supabase dashboard:
      const createTableSQL = `
        CREATE TABLE IF NOT EXISTS guides (
          id TEXT PRIMARY KEY,
          first_name TEXT NOT NULL,
          last_name TEXT NOT NULL,
          email TEXT NOT NULL,
          phone TEXT,
          bio TEXT,
          experience TEXT,
          languages JSONB DEFAULT '[]'::jsonb,
          specialties JSONB DEFAULT '[]'::jsonb,
          rating DECIMAL(3,1) DEFAULT 5.0,
          avatar TEXT,
          certifications JSONB DEFAULT '[]'::jsonb,
          location TEXT,
          availability BOOLEAN DEFAULT true,
          emergency_contact JSONB DEFAULT '{"name": "", "phone": "", "relationship": ""}'::jsonb,
          active BOOLEAN DEFAULT true,
          created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
          updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW()
        );
        
        -- Create indexes for better performance
        CREATE INDEX IF NOT EXISTS idx_guides_active ON guides(active);
        CREATE INDEX IF NOT EXISTS idx_guides_specialties ON guides USING GIN(specialties);
        CREATE INDEX IF NOT EXISTS idx_guides_rating ON guides(rating);
      `;

      console.log('📋 Please run this SQL in your Supabase SQL Editor:');
      console.log(createTableSQL);

      return { success: false, message: 'Please create the guides table using the provided SQL' };
    } else if (error) {
      console.error('❌ Error checking guides table:', error);
      return { success: false, error };
    } else {
      console.log('✅ Guides table already exists');
      return { success: true, message: 'Guides table exists' };
    }
  } catch (error) {
    console.error('❌ Error in createGuidesTable:', error);
    return { success: false, error };
  }
};

// Create tours table if it doesn't exist
export const createToursTable = async (): Promise<void> => {
  console.log('🔧 Creating tours table...');

  try {
    // Check if table exists by trying to query it
    const { error: checkError } = await supabase
      .from('tours')
      .select('id')
      .limit(1);

    if (!checkError) {
      console.log('✅ Tours table already exists');
      return;
    }

    console.log('📋 Tours table not found - manual setup required');
    console.log('⚠️ Please run the SQL script in your Supabase dashboard:');
    console.log('   File: create_tours_table.sql');
    console.log('   This will create the tours table and insert sample data');

    throw new Error('Tours table does not exist - manual setup required');

  } catch (error) {
    if (error instanceof Error && error.message.includes('manual setup required')) {
      // This is expected - just log the instructions
      return;
    }
    console.error('❌ Failed to check tours table:', error);
    throw error;
  }
};

// Migration helper - this will help you move to your own backend later
export const migrateToCustomBackend = async (customApiUrl: string) => {
  // This function will help you migrate data to your custom backend
  console.log('Migration helper ready for custom backend at:', customApiUrl);

  // You can implement the migration logic here when you're ready
  // This will make it easy to switch from Supabase to your own backend
};

export default supabaseTourService;
